import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthProvider";
import { api } from "../api/client";

const ROLE_LABEL: Record<string, string> = {
  management: "Management",
  staff: "Staff",
  maintenance: "Maintenance",
};

export function Topbar({ onToggleNav }: { onToggleNav: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Poll the alerts count every 5 minutes. Cheap query (single COUNT(*));
  // the cron only generates once a day so anything faster is wasted work.
  const countQ = useQuery({
    queryKey: ["alerts", "count"],
    queryFn: () => api.alerts.count(),
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  if (!user) return null;

  const isMaintenance = user.role === "maintenance";
  const greet = isMaintenance ? "My assigned work" : `Welcome, ${firstName(user.fullName)}`;
  const count = countQ.data?.count ?? 0;

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button
          type="button"
          className="nav-toggle"
          onClick={onToggleNav}
          aria-label="Show or hide the menu"
          title="Show or hide the menu"
        >
          <span aria-hidden="true">&#9776;</span>
        </button>
        <span className="topbar-greet">{greet}</span>
      </div>
      <div className="topbar-right">
        <Link
          to="/alerts"
          className="alert-bell"
          aria-label={
            count > 0 ? `You have ${count} open alert${count === 1 ? "" : "s"}` : "No open alerts"
          }
          title="Alerts"
        >
          <span aria-hidden="true">🔔</span>
          {count > 0 && <span className="alert-bell-badge">{count}</span>}
        </Link>
        <span className="user-chip">
          {firstName(user.fullName)} &middot; {ROLE_LABEL[user.role] ?? user.role}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onLogout}>
          Log out
        </button>
      </div>
    </header>
  );
}

function firstName(full: string): string {
  return full.split(/\s+/)[0] ?? full;
}
